
import { useState, useEffect } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useDroppable,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  Box,
  HStack,
  VStack,
  Text,
  IconButton,
} from '@chakra-ui/react'
import { Menu } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { RightSidebar } from './RightSidebar'
import { SortableBlock } from './SortableBlock'
import { ColumnBlock } from './ColumnBlock'
import { CanvasDropZone } from './CanvasDropZone'
import type { PageBlock, BlockType, PageLayoutStructure } from '@/types/pageBuilder'

interface PageBuilderProps {
  value?: PageLayoutStructure | null
  onChange?: (structure: PageLayoutStructure) => void
}

const blockDefinitions: Record<BlockType, Record<string, any>> = {
  heading: { text: 'Heading', level: 2, alignment: 'left' },
  text: { text: 'Enter your text here...', alignment: 'left' },
  image: { url: '', alt: '', caption: '', alignment: 'center', width: '100%' },
  video: { url: '', caption: '', alignment: 'center', autoplay: false, controls: true },
  button: { text: 'Button', url: '#', style: 'primary', alignment: 'left' },
  columns: { columns: 2, gap: 'medium', blocks: [] },
  quote: { text: 'Quote text', author: '', alignment: 'left' },
  list: { items: ['Item 1', 'Item 2'], type: 'ul', alignment: 'left' },
  spacer: { height: 40 },
  divider: { style: 'solid' },
  code: { code: '', language: 'javascript' },
  html: { html: '' },
}

export function PageBuilder({ value, onChange }: PageBuilderProps) {
  const colors = useThemeColors()
  const [blocks, setBlocks] = useState<PageBlock[]>(value?.blocks || [])
  const [selectedBlock, setSelectedBlock] = useState<PageBlock | null>(null)
  const [selectedColumnBlock, setSelectedColumnBlock] = useState<{ columnIndex: number; block: PageBlock } | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(true)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Sync blocks with value prop
  useEffect(() => {
    if (value?.blocks) {
      // Ensure column blocks have proper structure
      const normalizedBlocks = value.blocks.map((block) => {
        if (block.type === 'columns') {
          const columnCount = block.content.columns || 2
          const columnBlocks = block.content.columnBlocks || []
          // Ensure we have arrays for all columns
          while (columnBlocks.length < columnCount) {
            columnBlocks.push([])
          }
          return {
            ...block,
            content: {
              ...block.content,
              columnBlocks: columnBlocks.slice(0, columnCount),
            },
          }
        }
        return block
      })
      setBlocks(normalizedBlocks)
    }
  }, [value])

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over) return

    // Handle dropping a new block from the library
    const activeId = active.id as string
    if (activeId.startsWith('block-type-')) {
      const blockType = activeId.replace('block-type-', '') as BlockType
      if (over.id === 'canvas-drop-zone' || blocks.length === 0) {
        addBlock(blockType)
        return
      }
      // Insert at specific position if dropping on a block
      const overBlock = blocks.find(b => b.id === over.id)
      if (overBlock) {
        const newBlock: PageBlock = {
          id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: blockType,
          content: { ...blockDefinitions[blockType] },
          order: overBlock.order,
        }
        const newBlocks = [...blocks]
        newBlocks.splice(overBlock.order, 0, newBlock)
        const updatedBlocks = newBlocks.map((block, index) => ({
          ...block,
          order: index,
        }))
        setBlocks(updatedBlocks)
        notifyChange(updatedBlocks)
        setSelectedBlock(newBlock)
        return
      }
    }

    // Handle reordering existing blocks
    if (over && active.id !== over.id && typeof active.id === 'string' && typeof over.id === 'string') {
      // Check if both are block IDs (not library items)
      const activeBlock = blocks.find(b => b.id === active.id)
      const overBlock = blocks.find(b => b.id === over.id)
      
      if (activeBlock && overBlock) {
        setBlocks((items) => {
          const oldIndex = items.findIndex((item) => item.id === active.id)
          const newIndex = items.findIndex((item) => item.id === over.id)
          const newBlocks = arrayMove(items, oldIndex, newIndex)
          // Update order
          const updatedBlocks = newBlocks.map((block, index) => ({
            ...block,
            order: index,
          }))
          notifyChange(updatedBlocks)
          return updatedBlocks
        })
      }
    }
  }

  const notifyChange = (updatedBlocks: PageBlock[]) => {
    if (onChange) {
      onChange({ blocks: updatedBlocks })
    }
  }

  const addBlock = (type: BlockType) => {
    const newBlock: PageBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      content: type === 'columns' 
        ? { ...blockDefinitions[type], columnBlocks: Array.from({ length: blockDefinitions[type].columns || 2 }).map(() => []) }
        : { ...blockDefinitions[type] },
      order: blocks.length,
    }
    const newBlocks = [...blocks, newBlock]
    setBlocks(newBlocks)
    notifyChange(newBlocks)
    setSelectedBlock(newBlock)
  }

  const addBlockToColumn = (columnBlockId: string, columnIndex: number, blockType: BlockType) => {
    const newBlocks = blocks.map((block) => {
      if (block.id === columnBlockId && block.type === 'columns') {
        const columnBlocks = block.content.columnBlocks || []
        const newNestedBlock: PageBlock = {
          id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: blockType,
          content: { ...blockDefinitions[blockType] },
          order: columnBlocks[columnIndex]?.length || 0,
          parentId: columnBlockId,
        }
        const updatedColumnBlocks = [...columnBlocks]
        if (!updatedColumnBlocks[columnIndex]) {
          updatedColumnBlocks[columnIndex] = []
        }
        updatedColumnBlocks[columnIndex] = [...updatedColumnBlocks[columnIndex], newNestedBlock]
        return {
          ...block,
          content: {
            ...block.content,
            columnBlocks: updatedColumnBlocks,
          },
        }
      }
      return block
    })
    setBlocks(newBlocks)
    notifyChange(newBlocks)
  }

  const deleteBlockFromColumn = (columnBlockId: string, columnIndex: number, blockId: string) => {
    const newBlocks = blocks.map((block) => {
      if (block.id === columnBlockId && block.type === 'columns') {
        const columnBlocks = block.content.columnBlocks || []
        const updatedColumnBlocks = [...columnBlocks]
        if (updatedColumnBlocks[columnIndex]) {
          updatedColumnBlocks[columnIndex] = updatedColumnBlocks[columnIndex].filter(
            (b: PageBlock) => b.id !== blockId
          )
        }
        return {
          ...block,
          content: {
            ...block.content,
            columnBlocks: updatedColumnBlocks,
          },
        }
      }
      return block
    })
    setBlocks(newBlocks)
    notifyChange(newBlocks)
    if (selectedColumnBlock?.block.id === blockId) {
      setSelectedColumnBlock(null)
    }
  }

  const updateBlockInColumn = (columnBlockId: string, columnIndex: number, blockId: string, content: Record<string, any>) => {
    const newBlocks = blocks.map((block) => {
      if (block.id === columnBlockId && block.type === 'columns') {
        const columnBlocks = block.content.columnBlocks || []
        const updatedColumnBlocks = [...columnBlocks]
        if (updatedColumnBlocks[columnIndex]) {
          updatedColumnBlocks[columnIndex] = updatedColumnBlocks[columnIndex].map((b: PageBlock) =>
            b.id === blockId ? { ...b, content } : b
          )
        }
        return {
          ...block,
          content: {
            ...block.content,
            columnBlocks: updatedColumnBlocks,
          },
        }
      }
      return block
    })
    setBlocks(newBlocks)
    notifyChange(newBlocks)
  }

  const selectBlockInColumn = (columnIndex: number, block: PageBlock) => {
    setSelectedColumnBlock({ columnIndex, block })
    setSelectedBlock(null)
    if (!sidebarOpen) {
      setSidebarOpen(true)
    }
  }

  const updateBlock = (blockId: string, content: Record<string, any>) => {
    const newBlocks = blocks.map((block) =>
      block.id === blockId ? { ...block, content } : block
    )
    setBlocks(newBlocks)
    notifyChange(newBlocks)
    // Update selected block if it's the one being edited
    if (selectedBlock?.id === blockId) {
      setSelectedBlock({ ...selectedBlock, content })
    }
  }

  const deleteBlock = (blockId: string) => {
    const newBlocks = blocks
      .filter((block) => block.id !== blockId)
      .map((block, index) => ({ ...block, order: index }))
    setBlocks(newBlocks)
    notifyChange(newBlocks)
    if (selectedBlock?.id === blockId) {
      setSelectedBlock(null)
    }
  }

  const handleSelectBlock = (block: PageBlock) => {
    setSelectedBlock(block)
    // Open sidebar if closed when selecting a block
    if (!sidebarOpen) {
      setSidebarOpen(true)
    }
  }

  const handleCloseSettings = () => {
    setSelectedBlock(null)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box
        minH="600px"
        h="100%"
        border="1px"
        borderColor={colors.border}
        borderRadius="md"
        bg={colors.cardBg}
        display="flex"
        overflow="hidden"
        position="relative"
      >
        {/* Full Width Canvas */}
        <Box
          flex="1"
          display="flex"
          flexDirection="column"
          bg={colors.sectionBg}
          overflowY="auto"
          position="relative"
          w="100%"
        >
          {/* Canvas Header */}
          <Box
            p={3}
            borderBottom="1px"
            borderColor={colors.border}
            bg={colors.cardBg}
            position="sticky"
            top={0}
            zIndex={10}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text fontSize="sm" fontWeight="semibold" color={colors.text}>
              Page Canvas
            </Text>
            <IconButton
              aria-label="Toggle sidebar"
              icon={<Menu size={18} />}
              size="sm"
              variant="ghost"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            />
          </Box>

          {/* Blocks Canvas */}
          <Box 
            flex="1" 
            p={4} 
            overflowY="auto"
            onClick={() => {
              // Toggle sidebar when clicking on empty canvas area
              // Blocks will stop propagation, so only empty space clicks reach here
              setSidebarOpen(!sidebarOpen)
            }}
            className="canvas-background"
            cursor="pointer"
          >
            <CanvasDropZone>
              {blocks.length === 0 ? (
                <Box
                  p={12}
                  border="2px dashed"
                  borderColor={colors.border}
                  borderRadius="md"
                  textAlign="center"
                  bg={colors.cardBg}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Text color={colors.textMuted} mb={2}>
                    Start building your page
                  </Text>
                  <Text fontSize="sm" color={colors.textMuted}>
                    {sidebarOpen 
                      ? 'Drag blocks from the right sidebar or click to add them. Click canvas to toggle sidebar.'
                      : 'Click anywhere in the canvas to open the sidebar and add blocks'}
                  </Text>
                </Box>
              ) : (
                <Box onClick={(e) => e.stopPropagation()}>
                  <SortableContext
                    items={blocks.map((block) => block.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <VStack spacing={3} align="stretch" maxW="1200px" mx="auto">
                      {blocks.map((block) => {
                        if (block.type === 'columns') {
                          return (
                            <ColumnBlock
                              key={block.id}
                              block={block}
                              onUpdate={(content) => updateBlock(block.id, content)}
                              onDelete={() => deleteBlock(block.id)}
                              isSelected={selectedBlock?.id === block.id}
                              onSelect={() => handleSelectBlock(block)}
                              onAddBlockToColumn={(columnIndex, blockType) => addBlockToColumn(block.id, columnIndex, blockType)}
                              onDeleteBlockFromColumn={(columnIndex, blockId) => deleteBlockFromColumn(block.id, columnIndex, blockId)}
                              onUpdateBlockInColumn={(columnIndex, blockId, content) => updateBlockInColumn(block.id, columnIndex, blockId, content)}
                              onSelectBlockInColumn={(columnIndex, nestedBlock) => selectBlockInColumn(columnIndex, nestedBlock)}
                              selectedColumnBlock={selectedColumnBlock}
                            />
                          )
                        }
                        return (
                          <SortableBlock
                            key={block.id}
                            block={block}
                            onSelect={() => handleSelectBlock(block)}
                            onDelete={() => deleteBlock(block.id)}
                            isSelected={selectedBlock?.id === block.id}
                          />
                        )
                      })}
                    </VStack>
                  </SortableContext>
                </Box>
              )}
            </CanvasDropZone>
          </Box>
        </Box>

        {/* Right Sidebar - Blocks & Settings */}
        <RightSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onSelectBlock={addBlock}
          selectedBlock={selectedColumnBlock?.block || selectedBlock}
          onUpdateBlock={(content) => {
            if (selectedColumnBlock) {
              // Find the parent column block
              const parentColumnBlock = blocks.find(b => 
                b.type === 'columns' && 
                b.content.columnBlocks?.[selectedColumnBlock.columnIndex]?.some((nb: PageBlock) => nb.id === selectedColumnBlock.block.id)
              )
              if (parentColumnBlock) {
                updateBlockInColumn(
                  parentColumnBlock.id,
                  selectedColumnBlock.columnIndex,
                  selectedColumnBlock.block.id,
                  content
                )
              }
            } else if (selectedBlock) {
              updateBlock(selectedBlock.id, content)
            }
          }}
          onCloseSettings={() => {
            setSelectedBlock(null)
            setSelectedColumnBlock(null)
          }}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
      </Box>
      <DragOverlay>
        {activeId ? (
          <Box
            p={4}
            bg={colors.cardBg}
            border="2px"
            borderColor="blue.500"
            borderRadius="md"
            boxShadow="lg"
            opacity={0.9}
            minW="200px"
          >
            <Text fontSize="sm" fontWeight="medium" color={colors.text}>
              {blocks.find((b) => b.id === activeId)?.type || 
               (typeof activeId === 'string' && activeId.startsWith('block-type-') 
                 ? activeId.replace('block-type-', '').charAt(0).toUpperCase() + activeId.replace('block-type-', '').slice(1)
                 : 'Block')}
            </Text>
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
